/**
 * Renders a confirmation dialog modal
 */

import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import { FormInput, FormField } from './elemental'
import { Modal } from 'antd'
import { Button } from '@lmc/ui-kit'
import Glyph from './Glyph'
import colors from '@lmc/common/constants'

const FinalConfirmationDialog = ({
  message,
  onCancel,
  onConfirmation,
  isConfirmed,
}) => (
  <Modal
    title={
      <h1 style={{ color: colors.red, ...styles.header }}>Are you sure?</h1>
    }
    open={isConfirmed}
    onOk={onConfirmation}
    onCancel={onCancel}
    footer={[
      <Button
        className="lmc-action-button"
        id="confirmation-dialog-secondary-action"
        onClick={onCancel}
        variant="default"
      >
        Cancel
      </Button>,
      <Button
        className="confirmation-dialog-primary-action"
        icon={<Glyph name="trashcan" />}
        onClick={onConfirmation}
        style={styles.deleteButton}
        variant="danger"
      >
        Delete
      </Button>,
    ]}
  >
    <div
      data-testid="final-confirmation-dialog-message"
      dangerouslySetInnerHTML={{ __html: `<p>${message}</p>` }}
    />
  </Modal>
)

class ConfirmationDialog extends Component {
  state = {
    deleteText: '',
    confirmed: false,
  }

  handleChange = (event) => {
    this.setState({
      [event.target.name]: event.target.value,
    })
  }

  toggleConfirmed = () => {
    this.setState({
      confirmed: !this.state.confirmed,
    })
  }

  renderDeleteInput() {
    const { confirmationType, verificationText, t: translate } = this.props
    return confirmationType === 'danger' ? (
      <div>
        <br />
        <FormField
          label={translate('components.confirmationDialog.deleteInstruction')}
          htmlFor="deleteText"
          style={styles.deleteInputContainer}
        >
          <FormInput
            placeholder={verificationText}
            autoFocus
            name="deleteText"
            onChange={this.handleChange}
            className="input-name-for-removal"
          />
        </FormField>
      </div>
    ) : null
  }

  render() {
    const {
      buttonText,
      buttonType,
      children,
      confirmationLabel,
      confirmationType,
      isOpen,
      onCancel,
      onConfirmation,
      title = 'Are you sure?',
      verificationText,
      finalConfirmationText,
      showFinalConfirmationStep,
      t: translate,
    } = this.props

    const isDisabled =
      confirmationType === 'danger' &&
      this.state.deleteText?.toLowerCase() !== verificationText?.toLowerCase()

    return showFinalConfirmationStep && this.state.confirmed ? (
      <FinalConfirmationDialog
        {...this.props}
        isConfirmed={this.state.confirmed}
        onCancel={() => {
          this.toggleConfirmed()
          onCancel()
        }}
        message={finalConfirmationText}
      />
    ) : (
      <Modal
        title={
          confirmationType === 'danger' ? (
            <h1 style={{ color: colors.orange, ...styles.header }}>{title}</h1>
          ) : (
            title
          )
        }
        open={isOpen}
        onOk={onConfirmation}
        // confirmLoading={confirmLoading}
        onCancel={onCancel}
        footer={[
          <Button
            id="confirmation-dialog-secondary-action"
            onClick={onCancel}
            variant="default"
          >
            Cancel
          </Button>,
          confirmationLabel ? (
            <Button
              disabled={isDisabled}
              onClick={
                showFinalConfirmationStep
                  ? this.toggleConfirmed
                  : onConfirmation
              }
              style={styles.genericConfirmButton}
              variant="danger"
            >
              {confirmationLabel}
            </Button>
          ) : (
            <Button
              className="confirmation-dialog-primary-action"
              dataTestId="confirm-modal-button"
              disabled={isDisabled}
              onClick={
                showFinalConfirmationStep
                  ? this.toggleConfirmed
                  : onConfirmation
              }
              style={buttonType ? styles.buttonContainer : styles.deleteButton}
              variant={buttonType ? 'primary' : 'danger'}
            >
              {buttonText ||
                (showFinalConfirmationStep
                  ? translate('components.confirmationDialog.next')
                  : translate('components.confirmationDialog.delete'))}
            </Button>
          ),
        ]}
      >
        {children}
        {this.renderDeleteInput()}
      </Modal>
    )
  }
}

const styles = {
  cancelButton: {
    background: 'Transparent',
    padding: '0px 15px',
    textShadow: 'none',
    transition: 'color .2s ease',
  },
  deleteButton: {
    color: 'white',
    textShadow: 'none',
    marginLeft: 8,
  },
  genericConfirmButton: {
    marginLeft: 8,
  },
  buttonContainer: {
    marginLeft: 8,
  },
  header: {
    fontWeight: 700,
    fontSize: 18,
  },
  deleteInputContainer: {
    marginBottom: 0,
  },
}

ConfirmationDialog.propTypes = {
  body: PropTypes.string,
  finalConfirmationText: PropTypes.string,
  confirmationLabel: PropTypes.string,
  confirmationType: PropTypes.oneOf([
    'danger',
    'primary',
    'success',
    'warning',
  ]),
  onCancel: PropTypes.func,
  onConfirmation: PropTypes.func,
  showFinalConfirmationStep: PropTypes.bool,
  verificationText: PropTypes.string,
}

ConfirmationDialog.defaultProps = {
  isOpen: false,
}

export default withTranslation()(ConfirmationDialog)
